import type {
  InventoryMovementReadModel,
  OrderDetail,
  OrderStrongReportAnomalyCode,
  OrderStrongReportOpeningMethod,
  PaymentMethod,
  PaymentStatus,
  TableState,
} from "./index.js";

export interface BusinessTerm {
  label: string;
  explanation: string;
  nextStep: string;
}

export const paymentMethodTerms = {
  CASH: {
    label: "现金",
    explanation: "顾客以现金付款，由工作人员在本机登记。",
    nextStep: "请把现金交接结果纳入当前班次核对。",
  },
  STATIC_WECHAT: {
    label: "微信静态码",
    explanation: "顾客扫门店固定微信收款码，系统需要随后核对渠道流水。",
    nextStep: "渠道确认前不要把这笔记录当作已到账。",
  },
  STATIC_ALIPAY: {
    label: "支付宝静态码",
    explanation: "顾客扫门店固定支付宝收款码，系统需要随后核对渠道流水。",
    nextStep: "渠道确认前不要把这笔记录当作已到账。",
  },
  MEMBER_BALANCE: {
    label: "会员余额",
    explanation: "使用顾客会员账户中的可用余额付款。",
    nextStep: "请以页面显示的扣款结果为准。",
  },
  WECHAT_PAY: {
    label: "微信支付",
    explanation: "系统向微信支付发起并查询这一笔付款。",
    nextStep: "请等待渠道明确结果，不要让顾客重复付款。",
  },
  ALIPAY: {
    label: "支付宝",
    explanation: "系统向支付宝发起并查询这一笔付款。",
    nextStep: "请等待渠道明确结果，不要让顾客重复付款。",
  },
} satisfies Record<PaymentMethod, BusinessTerm>;

export const tableStateTerms = {
  IDLE: {
    label: "空闲",
    explanation: "这张球台当前没有活动台次，可以接待新顾客。",
    nextStep: "开台前请核对顾客需求和当前价格方案。",
  },
  IN_USE: {
    label: "使用中",
    explanation: "这张球台已有正在营业的台次和订单。",
    nextStep: "请打开本桌明细继续加项目、计费、收款或处理服务。",
  },
  AWAITING_CLEANING: {
    label: "待清台",
    explanation: "本桌账单已经结束，但球台尚未完成清洁确认。",
    nextStep: "请确认清洁完成后再接待下一位顾客。",
  },
  DISABLED: {
    label: "不可用",
    explanation: "这张球台当前已停用，不能建立新台次。",
    nextStep: "如需恢复，请由店长核对球台配置和设备状态。",
  },
} satisfies Record<TableState, BusinessTerm>;

export const paymentStatusTerms = {
  CONFIRMED_LOCAL: {
    label: "现金已登记",
    explanation: "工作人员已在本机登记收到现金，这不是线上渠道到账证明。",
    nextStep: "请在交班时核对实收现金。",
  },
  CONFIRMED_CLOUD: {
    label: "渠道已确认到账",
    explanation: "支付渠道已返回这笔付款成功的确认结果。",
    nextStep: "可以按订单当前状态继续后续操作。",
  },
  PENDING_RECONCILIATION: {
    label: "等待渠道核对",
    explanation: "系统还没有拿到足以确认到账的渠道结果。",
    nextStep: "不要重复收款，请保留原记录并等待核对。",
  },
  PENDING_MEMBER_DEBIT: {
    label: "会员余额等待联网扣除",
    explanation: "会员扣款请求已保留，但当前还没有确认余额扣除成功。",
    nextStep: "恢复联网后继续核对原请求，不要重复扣款。",
  },
  MEMBER_RECEIVABLE: {
    label: "会员余额待补扣",
    explanation: "这笔消费已形成会员应收，但会员余额尚未完成扣除。",
    nextStep: "请由负责人按待处理记录完成补扣或纠错。",
  },
  MANUALLY_RECONCILED: {
    label: "负责人已人工核对一致",
    explanation: "负责人已根据收款证据确认系统记录与实际收款一致。",
    nextStep: "后续如需追查，请查看原收款记录和操作记录。",
  },
  RECONCILIATION_EXCEPTION: {
    label: "收款核对异常",
    explanation: "收款记录与渠道或门店证据不一致，当前不能自动确认。",
    nextStep: "请保留订单并交由负责人核对，避免再次收款。",
  },
  PARTIALLY_REFUNDED: {
    label: "已部分退款",
    explanation: "这笔收款只有一部分金额已经退回。",
    nextStep: "请核对退款金额和订单剩余实收。",
  },
  REFUNDED: {
    label: "已全部退款",
    explanation: "这笔收款的可退金额已经全部退回。",
    nextStep: "如需追查，请查看退款记录和授权信息。",
  },
} satisfies Record<PaymentStatus, BusinessTerm>;

export const orderStatusTerms = {
  OPEN: {
    label: "订单未结账",
    explanation: "这张订单仍在进行中，金额和项目还可能变化。",
    nextStep: "可以继续加项目、收款或按页面流程结账。",
  },
  SETTLED: {
    label: "订单已结账",
    explanation: "这张订单已经完成结账，实收和退款应按记录核对。",
    nextStep: "需要售后时，请从原订单发起退款或退货。",
  },
  REFUNDED: {
    label: "订单已全部退款",
    explanation: "这张订单的已收金额已经全部退回。",
    nextStep: "如需追查，请查看退款记录和授权信息。",
  },
  CONFLICT: {
    label: "订单待人工复核",
    explanation: "这张订单出现互相不一致的记录，当前页面不能替负责人作出结论。",
    nextStep: "请保留现场并在待处理记录中核对这一单。",
  },
} satisfies Record<OrderDetail["status"], BusinessTerm>;

export const syncStateTerms = {
  PENDING: {
    label: "记录等待同步",
    explanation: "这笔记录已保留在门店，正在等待写入云端。",
    nextStep: "可以继续当前经营操作，恢复联网后系统会继续同步。",
  },
  SYNCED: {
    label: "记录已同步",
    explanation: "这笔记录已经写入当前权威数据源。",
    nextStep: "可以按当前订单状态继续操作。",
  },
  CONFLICT: {
    label: "记录待人工复核",
    explanation: "只有这一笔记录存在冲突，不能据此判断整套系统离线。",
    nextStep: "请在待处理记录中核对这一笔，其他经营功能可继续使用。",
  },
  FAILED: {
    label: "同步失败，等待处理",
    explanation: "只有这一笔记录本次同步失败，系统会保留原记录。",
    nextStep: "请按页面提示重试或交由负责人处理，避免重复录入。",
  },
  ISOLATED: {
    label: "记录已隔离，尚未写入云端",
    explanation: "只有这一笔需要复核的记录被隔离，整套经营系统并未因此离线。",
    nextStep: "请在待处理记录中核对这一笔，其他经营功能可继续使用。",
  },
} satisfies Record<OrderDetail["syncState"], BusinessTerm>;

export const orderOpeningMethodTerms = {
  DIRECT: {
    label: "工作人员直接开台",
    explanation: "工作人员在前台直接为顾客开台。",
    nextStep: "核对时可回到开台操作记录查看经办人。",
  },
  RESERVATION: {
    label: "预约入座开台",
    explanation: "顾客到店后由已有预约转为开台。",
    nextStep: "核对时请同时查看页面显示的预约单号。",
  },
  GROUP_BUY_ONLINE: {
    label: "在线团购核销开台",
    explanation: "线上团购券核销成功后形成开台记录。",
    nextStep: "核对时请同时查看页面显示的团购核销单号。",
  },
  UNKNOWN: {
    label: "开台来源待核对",
    explanation: "当前记录不足以确认这张订单是怎样开台的。",
    nextStep: "请按异常提示回到原订单和操作记录核对。",
  },
} satisfies Record<OrderStrongReportOpeningMethod, BusinessTerm>;

export const orderAnomalyTerms = {
  ORDER_OPEN: {
    label: "订单尚未结账",
    explanation: "订单仍在进行中，当前金额不是最终结账金额。",
    nextStep: "请回到原订单继续处理或完成结账。",
  },
  PAYMENT_PENDING: {
    label: "收款等待渠道核对",
    explanation: "渠道尚未给出足以确认到账的结果。",
    nextStep: "请核对原付款，不要让顾客重复付款。",
  },
  PAYMENT_EXCEPTION: {
    label: "收款核对异常",
    explanation: "收款记录与现有渠道或门店证据不一致。",
    nextStep: "请保留订单并交由负责人核对。",
  },
  UNEXPLAINED_DIFFERENCE: {
    label: "存在未说明的金额差异",
    explanation: "订单应收、已确认实收、退款或调整之间仍有差额。",
    nextStep: "请逐项核对原订单、付款、退款和调整记录。",
  },
  ORDER_CONFLICT: {
    label: "订单记录待人工复核",
    explanation: "这一张订单存在互相不一致的记录。",
    nextStep: "请在待处理记录中核对这一单，其他订单不受影响。",
  },
  SYNC_FAILED: {
    label: "这笔记录同步失败",
    explanation: "只有这一笔记录本次同步失败，原记录仍然保留。",
    nextStep: "请按页面提示重试或交由负责人处理。",
  },
  SYNC_ISOLATED: {
    label: "这笔记录已隔离",
    explanation: "只有这一笔需要复核的记录尚未写入云端。",
    nextStep: "请核对这一笔，其他经营功能可继续使用。",
  },
  OPENING_SOURCE_UNKNOWN: {
    label: "开台来源待核对",
    explanation: "当前记录不足以确认订单的开台来源。",
    nextStep: "请回到原订单和操作记录核对。",
  },
} satisfies Record<OrderStrongReportAnomalyCode, BusinessTerm>;

export const inventoryMovementTerms = {
  PURCHASE_IN: {
    label: "采购入库",
    explanation: "供应商送货验收后，通过库存审批增加商品库存。",
    nextStep: "请核对申请、审批和实际入库数量。",
  },
  OTHER_IN: {
    label: "其他入库",
    explanation: "商品因调拨转入、赠品等非采购原因增加库存。",
    nextStep: "请核对入库原因、经办人和实际增加数量。",
  },
  RETURN_OUT: {
    label: "供应商退货出库",
    explanation: "商品退还供应商后从门店库存中扣减。",
    nextStep: "请核对退货原因、供应商和实际扣减数量。",
  },
  LOSS_OUT: {
    label: "报损出库",
    explanation: "商品因损坏、过期或遗失确认报损后减少库存。",
    nextStep: "请核对报损原因、审批记录和库存价值变化。",
  },
  INTERNAL_USE_OUT: {
    label: "门店领用出库",
    explanation: "商品用于门店内部经营后从可售库存中扣减。",
    nextStep: "请核对领用原因、经办人和实际领用数量。",
  },
  COUNT_SET: {
    label: "库存盘点调整",
    explanation: "完成实物盘点后，系统按确认的账实差调整库存。",
    nextStep: "请核对实盘数量、审批记录和调整后的库存。",
  },
  MANUAL_ADJUSTMENT: {
    label: "人工库存调整",
    explanation: "负责人按明确原因直接增加或减少商品库存。",
    nextStep: "请核对调整原因、授权记录和调整前后数量。",
  },
  PURCHASE_RECEIPT_IN: {
    label: "采购收货入库",
    explanation: "已批准采购单完成本次收货后，商品库存按实际到货数量增加。",
    nextStep: "请核对采购单、收货单和本次入库数量。",
  },
  SALE_OUT: {
    label: "销售扣减库存",
    explanation: "商品已作为销售或桌边送达的业务结果扣减库存。",
    nextStep: "如需退回，请从原销售记录发起退货，不能直接改库存。",
  },
  SALE_RETURN: {
    label: "销售退回库存",
    explanation: "原销售退货确认后，可恢复的商品数量已经加回库存。",
    nextStep: "请核对原销售记录、退货记录和本次恢复数量。",
  },
} satisfies Record<InventoryMovementReadModel["type"], BusinessTerm>;

export function paymentMethodTerm(method: PaymentMethod): BusinessTerm {
  return paymentMethodTerms[method];
}

export function tableStateTerm(state: TableState): BusinessTerm {
  return tableStateTerms[state];
}

export function paymentStatusTerm(status: PaymentStatus): BusinessTerm {
  return paymentStatusTerms[status];
}

export function orderStatusTerm(status: OrderDetail["status"]): BusinessTerm {
  return orderStatusTerms[status];
}

export function syncStateTerm(state: OrderDetail["syncState"]): BusinessTerm {
  return syncStateTerms[state];
}

export function orderOpeningMethodTerm(method: OrderStrongReportOpeningMethod): BusinessTerm {
  return orderOpeningMethodTerms[method];
}

export function orderAnomalyTerm(code: OrderStrongReportAnomalyCode): BusinessTerm {
  return orderAnomalyTerms[code];
}

export function inventoryMovementTerm(type: InventoryMovementReadModel["type"]): BusinessTerm {
  return inventoryMovementTerms[type];
}
